import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useViewOnly } from '../contexts';
import './UserMenu.css';

const UserMenu = ({ language = 'he', onNavigate, onProfileClick, onPasswordClick, onUsersClick, onAuditClick }) => {
  const { user, logout, isAdmin } = useAuth();
  const { isViewOnly, setViewOnly } = useViewOnly();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  const handleItemClick = (callback) => {
    setIsOpen(false);
    if (callback) callback();
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: { en: 'Administrator', he: 'מנהל' },
      doctor: { en: 'Doctor', he: 'רופא' },
      receptionist: { en: 'Receptionist', he: 'קבלה' },
      assistant: { en: 'Assistant', he: 'עוזר' },
    };
    return labels[role]?.[language] || role;
  };

  const getInitials = () => {
    if (!user) return '?';
    const first = user.first_name?.charAt(0) || '';
    const last = user.last_name?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  if (!user) return null;

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className={`user-menu-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="user-avatar">
          {getInitials()}
        </div>
        <div className="user-info">
          <span className="user-name">{user.first_name} {user.last_name}</span>
          <span className="user-role">{getRoleLabel(user.role)}</span>
        </div>
        <span className="dropdown-arrow">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="dropdown-header">
            <div className="header-avatar">{getInitials()}</div>
            <div className="header-info">
              <span className="header-name">{user.first_name} {user.last_name}</span>
              <span className="header-email">{user.email}</span>
            </div>
          </div>

          <div className="dropdown-divider"></div>

          <div className="dropdown-items">
            <button
              className="dropdown-item"
              onClick={() => handleItemClick(onProfileClick)}
            >
              <span className="item-icon">&#128100;</span>
              <span>{language === 'he' ? 'הפרופיל שלי' : 'My Profile'}</span>
            </button>

            <button
              className="dropdown-item"
              onClick={() => handleItemClick(onPasswordClick)}
            >
              <span className="item-icon">&#128274;</span>
              <span>{language === 'he' ? 'שינוי סיסמה' : 'Change Password'}</span>
            </button>

            <button
              className={`dropdown-item view-only-toggle ${isViewOnly ? 'is-on' : ''}`}
              onClick={() => setViewOnly(!isViewOnly)}
              title={
                language === 'he'
                  ? 'מנע פעולות כתיבה (יצירה / עדכון / מחיקה) במצב זה'
                  : 'Disable write actions (create / update / delete) in this mode'
              }
            >
              <span className="item-icon">{isViewOnly ? '🔒' : '🔓'}</span>
              <span style={{ flex: 1 }}>
                {language === 'he' ? 'מצב צפייה בלבד' : 'View-only mode'}
              </span>
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 16,
                  borderRadius: 8,
                  background: isViewOnly ? '#dc2626' : '#cbd5e0',
                  position: 'relative',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: isViewOnly ? 14 : 2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s',
                  }}
                />
              </span>
            </button>

            {isAdmin && (
              <>
                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item"
                  onClick={() => handleItemClick(() => onNavigate && onNavigate('settings'))}
                >
                  <span className="item-icon">&#9881;</span>
                  <span>{language === 'he' ? 'הגדרות' : 'Settings'}</span>
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => handleItemClick(onUsersClick)}
                >
                  <span className="item-icon">&#128101;</span>
                  <span>{language === 'he' ? 'ניהול משתמשים' : 'Manage Users'}</span>
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => handleItemClick(onAuditClick)}
                >
                  <span className="item-icon">&#128202;</span>
                  <span>{language === 'he' ? 'יומן פעולות' : 'Audit Log'}</span>
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => handleItemClick(() => onNavigate && onNavigate('qa'))}
                >
                  <span className="item-icon">&#128269;</span>
                  <span>{language === 'he' ? 'בדיקות QA' : 'QA Testing'}</span>
                </button>
              </>
            )}

            <div className="dropdown-divider"></div>

            <button className="dropdown-item logout" onClick={handleLogout}>
              <span className="item-icon">&#128682;</span>
              <span>{language === 'he' ? 'התנתק' : 'Logout'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
